/**
 * staffMarketplaceStore — Staff marketplace slice (Zustand).
 *
 *   GET   /api/staff/marketplace/purchases
 *   POST  /api/staff/marketplace/purchases/:transactionId/revoke
 *   POST  /api/staff/marketplace/purchases/:transactionId/restore
 *
 *   GET   /api/staff/marketplace/:type
 *   GET   /api/staff/marketplace/:type/:id
 *   PATCH /api/staff/marketplace/:type/:id
 *   PATCH /api/staff/marketplace/:type/:id/approve
 *   PATCH /api/staff/marketplace/:type/:id/reject
 *   PATCH /api/staff/marketplace/:type/:id/restore
 *   PATCH /api/staff/marketplace/:type/:id/withdraw
 *
 * `:type` is one of: project-topics, seminar-topics, research-materials, manuals.
 */
import { apiRequest, normalizeList } from "./staffUtils";
import { toDisplayString } from "../../utils/toDisplayString";

export const staffMarketplaceInitialState = {
  // Resources
  staffMarketplaceResources: [],
  staffMarketplaceResourcesType: "project-topics",
  staffMarketplaceResourcesLoading: false,
  staffMarketplaceResourcesError: null,
  staffMarketplaceResourcesTotal: 0,
  staffMarketplaceResourcesPages: 1,

  // Single resource
  staffMarketplaceResourceDetail: null,
  staffMarketplaceResourceDetailLoading: false,
  staffMarketplaceResourceDetailError: null,

  // Purchases
  staffMarketplacePurchases: [],
  staffMarketplacePurchasesLoading: false,
  staffMarketplacePurchasesError: null,
  staffMarketplacePurchasesTotal: 0,
  staffMarketplacePurchasesPages: 1,

  // Mutations
  staffMarketplaceMutationLoading: false,
  staffMarketplaceMutationError: null,
};

/** Drop empty query values so strict list schemas are never sent blanks. */
function buildQuery(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.set(key, String(value));
  });
  const qs = query.toString();
  return qs ? `?${qs}` : "";
}

export function createStaffMarketplaceActions(set, get) {
  async function mutate(path, method, body, successMessage) {
    set({
      staffMarketplaceMutationLoading: true,
      staffMarketplaceMutationError: null,
    });
    try {
      const data = await apiRequest(get, path, { method, body });
      set({
        staffMarketplaceMutationLoading: false,
        staffMarketplaceMutationError: null,
        successMessage,
      });
      return { success: true, data: data?.data ?? data };
    } catch (err) {
      const message = toDisplayString(err.message, "Marketplace action failed");
      set({
        staffMarketplaceMutationLoading: false,
        staffMarketplaceMutationError: message,
      });
      return { success: false, error: message };
    }
  }

  return {
    // ── Resources ───────────────────────────────────────────────────────────
    // GET /api/staff/marketplace/:type
    fetchStaffMarketplaceResources: async (type, params = {}) => {
      set({
        staffMarketplaceResourcesLoading: true,
        staffMarketplaceResourcesError: null,
        staffMarketplaceResourcesType: type,
      });
      try {
        const data = await apiRequest(
          get,
          `/api/staff/marketplace/${type}${buildQuery(params)}`
        );
        const { items, total, totalPages } = normalizeList(data, ["resources"]);
        set({
          staffMarketplaceResourcesLoading: false,
          staffMarketplaceResources: items,
          staffMarketplaceResourcesTotal: total,
          staffMarketplaceResourcesPages: totalPages,
        });
        return { success: true, resources: items, total, pages: totalPages };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to fetch marketplace resources");
        set({
          staffMarketplaceResourcesLoading: false,
          staffMarketplaceResourcesError: message,
          staffMarketplaceResources: [],
        });
        return { success: false, error: message };
      }
    },

    // GET /api/staff/marketplace/:type/:id
    fetchStaffMarketplaceResource: async (type, id) => {
      set({
        staffMarketplaceResourceDetailLoading: true,
        staffMarketplaceResourceDetailError: null,
      });
      try {
        const data = await apiRequest(get, `/api/staff/marketplace/${type}/${id}`);
        const detail = data?.data ?? data ?? null;
        set({
          staffMarketplaceResourceDetailLoading: false,
          staffMarketplaceResourceDetail: detail,
        });
        return { success: true, resource: detail };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to fetch marketplace resource");
        set({
          staffMarketplaceResourceDetailLoading: false,
          staffMarketplaceResourceDetailError: message,
          staffMarketplaceResourceDetail: null,
        });
        return { success: false, error: message };
      }
    },

    // PATCH /api/staff/marketplace/:type/:id
    updateStaffMarketplaceResource: (type, id, payload = {}) =>
      mutate(
        `/api/staff/marketplace/${type}/${id}`,
        "PATCH",
        payload,
        "Marketplace resource updated"
      ),

    // PATCH /api/staff/marketplace/:type/:id/approve
    approveStaffMarketplaceResource: (type, id) =>
      mutate(
        `/api/staff/marketplace/${type}/${id}/approve`,
        "PATCH",
        undefined,
        "Marketplace resource approved"
      ),

    // PATCH /api/staff/marketplace/:type/:id/reject
    rejectStaffMarketplaceResource: (type, id, reason = "") =>
      mutate(
        `/api/staff/marketplace/${type}/${id}/reject`,
        "PATCH",
        { reason },
        "Marketplace resource rejected"
      ),

    // PATCH /api/staff/marketplace/:type/:id/restore
    restoreStaffMarketplaceResource: (type, id) =>
      mutate(
        `/api/staff/marketplace/${type}/${id}/restore`,
        "PATCH",
        undefined,
        "Marketplace resource restored"
      ),

    // PATCH /api/staff/marketplace/:type/:id/withdraw
    withdrawStaffMarketplaceResource: (type, id) =>
      mutate(
        `/api/staff/marketplace/${type}/${id}/withdraw`,
        "PATCH",
        undefined,
        "Marketplace resource withdrawn"
      ),

    // ── Purchases ───────────────────────────────────────────────────────────
    // GET /api/staff/marketplace/purchases
    fetchStaffMarketplacePurchases: async (params = {}) => {
      set({ staffMarketplacePurchasesLoading: true, staffMarketplacePurchasesError: null });
      try {
        const data = await apiRequest(
          get,
          `/api/staff/marketplace/purchases${buildQuery(params)}`
        );
        const { items, total, totalPages } = normalizeList(data, ["purchases"]);
        set({
          staffMarketplacePurchasesLoading: false,
          staffMarketplacePurchases: items,
          staffMarketplacePurchasesTotal: total,
          staffMarketplacePurchasesPages: totalPages,
        });
        return { success: true, purchases: items, total, pages: totalPages };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to fetch marketplace purchases");
        set({
          staffMarketplacePurchasesLoading: false,
          staffMarketplacePurchasesError: message,
          staffMarketplacePurchases: [],
        });
        return { success: false, error: message };
      }
    },

    // POST /api/staff/marketplace/purchases/:transactionId/revoke
    revokeStaffMarketplacePurchase: (transactionId, reason = "") =>
      mutate(
        `/api/staff/marketplace/purchases/${transactionId}/revoke`,
        "POST",
        { reason },
        "Purchase access revoked"
      ),

    // POST /api/staff/marketplace/purchases/:transactionId/restore
    restoreStaffMarketplacePurchase: (transactionId) =>
      mutate(
        `/api/staff/marketplace/purchases/${transactionId}/restore`,
        "POST",
        undefined,
        "Purchase access restored"
      ),
  };
}

const staffMarketplaceActions = createStaffMarketplaceActions;
export default staffMarketplaceActions;
